import { RouterProvider } from 'react-router-dom';

// project import
import router from 'routes';
import ThemeCustomization from 'themes';
import useConfig from 'hooks/useConfig';
import { useHybridVersionCheck } from 'hooks/useHybridVersionCheck';
import { useVersionInfoReporter } from 'hooks/useVersionInfoReporter';

import Locales from 'components/Locales';
import ScrollTop from 'components/ScrollTop';
import Snackbar from 'components/@extended/Snackbar';
import Notistack from 'components/third-party/Notistack';
import VersionUpdateNotification from 'components/VersionUpdateNotification';
import MandatoryReloadDialog from 'components/MandatoryReloadDialog';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';

// ==============================|| APP - THEME, ROUTER, LOCAL ||============================== //

const App = () => {
  // Hybrid version checking: SignalR push + polling fallback
  const {
    hasNewVersion,
    currentVersion,
    newVersion,
    isMandatory,
    countdownSeconds,
    message,
    source,
    reloadApp,
    clearUpdate,
    forceCheck
  } = useHybridVersionCheck();

  // Automatically respond to admin version info requests
  useVersionInfoReporter();
  
  // Expose forceCheck globally for debug tools (all environments)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any)._versionCheckForceCheck = forceCheck;
    }
  }, [forceCheck]);

  // Track if user cancelled mandatory reload
  const [mandatoryCancelled, setMandatoryCancelled] = useState(false);

  const handleCancelMandatory = () => {
    console.log('[App] User cancelled mandatory reload');
    setMandatoryCancelled(true);
    clearUpdate();
  };

  // Reset cancelled state when new update arrives
  useEffect(() => {
    if (hasNewVersion && isMandatory) {
      setMandatoryCancelled(false);
    }
  }, [hasNewVersion, isMandatory]);

  return (
    <ThemeCustomization>
      <Locales>
        <ScrollTop>
          <>
            <Notistack>
              <RouterProvider router={router} />
              <Snackbar />
            </Notistack>
            <ToastContainerWrapper />
            
            {/* Mandatory Update Dialog (with countdown) */}
            {isMandatory && !mandatoryCancelled && (
              <MandatoryReloadDialog
                open={hasNewVersion}
                version={newVersion}
                message={message}
                countdownSeconds={countdownSeconds}
                onReload={reloadApp}
                onCancel={handleCancelMandatory}
              />
            )}

            {/* Optional Update Notification (snackbar) */}
            {!isMandatory && (
              <VersionUpdateNotification
                hasNewVersion={hasNewVersion}
                currentVersion={currentVersion}
                newVersion={newVersion}
                message={message}
                source={source}
                onReload={reloadApp}
                mode="snackbar"
              />
            )}
          </>
        </ScrollTop>
      </Locales>
    </ThemeCustomization>
  );
};

// ToastContainer wrapper that follows the theme
const ToastContainerWrapper = () => {
  const { mode } = useConfig();
  
  // Determine theme for react-toastify
  const isClassicTheme = mode === 'classic';
  const toastTheme = isClassicTheme ? 'dark' : (mode === 'dark' ? 'dark' : 'light');
  
  // Inject CSS to fix toast positioning
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .Toastify__toast-container--top-right {
        top: 16px !important;
        right: 8px !important;
        max-width: 1360px !important;
        max-height: 1237px !important;
      }
      .Toastify__toast {
        max-width: 400px !important;
        min-width: 300px !important;
        right: 8px !important;
        left: auto !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  return (
    <ToastContainer
      theme={toastTheme}
      position="top-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      toastStyle={{
        ...(isClassicTheme && {
          backgroundColor: '#000000',
          color: '#FFD700',
          border: '1px solid #404040'
        }),
        maxWidth: '400px !important',
        minWidth: '300px !important',
        right: '8px !important',
        left: 'auto !important'
      }}
      progressStyle={{
        ...(isClassicTheme && {
          backgroundColor: '#FFD700'
        })
      }}
      style={{
        top: '16px',
        right: '8px',
        maxWidth: '1360px',
        maxHeight: '1237px',
        zIndex: 9999
      }}
    />
  );
};

export default App;
